import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button, RollingNumber } from "../components/Bits";
import Editable from "../components/Editable";
import EditableImage from "../components/EditableImage";
import { IMG, STATS, VALUES } from "../lib/content";

const approach = [
  { title: "Collaborative by design", body: "We work hand-in-hand with public sector partners, businesses, schools, charities and community groups to co-create solutions." },
  { title: "Ambitious yet grounded", body: "We deliver ambitious projects that combine genuine social impact with long-term sustainability and responsible finance." },
  { title: "Surplus reinvested", body: "As a CIC, every surplus we generate is reinvested into our charitable objectives, expanding our reach and impact." },
];

export default function About() {
  return (
    <div>
      <PageHeader label="About NEAD" titleLines={["A purpose-driven", "Community Interest", "Company."]}
        intro="NEAD CIC, short for Northern Equality and Diversity, designs and delivers community programmes, festivals, live music and youth provision across Cumbria." />

      {/* Statement */}
      <section className="container-x pt-24 md:pt-32 pb-4">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="font-display font-bold text-nead-pink">01</span>
            <span className="h-px flex-1 bg-black/[0.07]" />
            <Eyebrow className="text-nead-navy/40">Who we are</Eyebrow>
          </div>
          <Editable id="about.statement" as="p" multiline className="mt-10 max-w-5xl font-display font-medium text-[1.9rem] sm:text-4xl lg:text-[2.9rem] leading-[1.2] tracking-tight text-nead-navy text-balance">
            We are a good CIC rooted in music, festivals and a dedicated under-18s centre, strengthening communities,
            creating opportunities for young people and promoting equality and inclusion right across Cumbria.
          </Editable>
        </Reveal>
      </section>

      {/* Mission */}
      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <EditableImage id="about.mission.image" src={IMG.boardroom2} alt="A strategy meeting" className="overflow-hidden rounded-[1.75rem] aspect-[4/5] block" imgClassName="h-full w-full object-cover" />
              <div className="absolute -bottom-6 -right-6 hidden md:block rounded-2xl grad-pink-cyan px-7 py-6 text-white shadow-xl">
                <div className="font-display font-extrabold text-3xl leading-none">Est. Cumbria</div>
                <div className="mt-1 text-sm text-white/85">Northern Equality &amp; Diversity</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow className="text-nead-pink">Our mission</Eyebrow>
            <Editable id="about.mission.heading" as="h2" className="mt-5 font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-nead-navy">
              Strengthening communities, creating opportunities and promoting equality.
            </Editable>
            <Editable id="about.mission.p1" as="p" multiline className="mt-7 text-nead-navy/65 text-lg leading-relaxed">
              We are a good CIC rooted in music, festivals and a dedicated under-18s centre. Our work
              focuses on strengthening communities, creating opportunities for young people, promoting
              equality and inclusion, and delivering sustainable projects that generate lasting social value.
            </Editable>
            <Editable id="about.mission.p2" as="p" multiline className="mt-4 text-nead-navy/65 leading-relaxed">
              From youth provision and community engagement to major cultural events and
              regeneration initiatives, our projects improve wellbeing and foster stronger,
              more connected communities.
            </Editable>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button to="/work" variant="pink" testId="about-see-work">Explore our work</Button>
              <Button to="/about/team" variant="outline" testId="about-team">Meet the team</Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How we work */}
      <section className="bg-[#070912] text-white border-y border-white/10">
        <div className="container-x py-24 md:py-32">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-display font-bold text-nead-cyan">02</span>
              <span className="h-px flex-1 bg-white/10" />
              <Eyebrow className="text-white/40">How we work</Eyebrow>
            </div>
            <h2 className="mt-10 font-display font-extrabold text-4xl sm:text-6xl tracking-tight max-w-3xl leading-[1.02]">A trusted delivery partner.</h2>
          </Reveal>
          <Stagger className="mt-16 grid md:grid-cols-3 gap-6">
            {approach.map((a, i) => (
              <StaggerItem key={a.title}>
                <div className="group h-full rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-nead-pink/40">
                  <span className="font-display font-extrabold text-5xl text-gradient">0{i + 1}</span>
                  <h3 className="mt-6 font-display font-bold text-xl">{a.title}</h3>
                  <p className="mt-3 text-white/60 leading-relaxed text-[0.95rem]">{a.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="mt-12"><Button to="/about/aims" variant="pink" testId="about-aims-link">Our aims &amp; purpose</Button></div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-nead-ink text-white">
        <div className="container-x py-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-14">
            {STATS.map((s, i) => (
              <Reveal key={s.label}>
                <div className={`font-display font-extrabold text-5xl lg:text-6xl leading-none ${i % 2 ? "text-nead-cyan" : "text-nead-pink"}`}>
                  <RollingNumber value={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-4 text-white/55 text-sm max-w-[11rem]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="container-x py-24 md:py-32">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="font-display font-bold text-nead-pink">03</span>
            <span className="h-px flex-1 bg-black/[0.07]" />
            <Eyebrow className="text-nead-navy/40">What we stand for</Eyebrow>
          </div>
          <h2 className="mt-10 font-display font-extrabold text-4xl sm:text-6xl tracking-tight text-nead-navy max-w-3xl leading-[1.02]">Our values.</h2>
        </Reveal>
        <Stagger className="mt-16 grid sm:grid-cols-2 gap-x-10 gap-y-12">
          {VALUES.map((v) => (
            <StaggerItem key={v.n}>
              <div className="flex gap-6">
                <span className="font-display font-extrabold text-4xl text-nead-navy/15">{v.n}</span>
                <div className="border-l border-black/[0.07] pl-6">
                  <h3 className="font-display font-bold text-2xl text-nead-navy">{v.title}</h3>
                  <p className="mt-3 text-nead-navy/60 leading-relaxed">{v.body}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>
    </div>
  );
}
